// The Seal language for Monaco: Monarch tokenizer, language configuration
// (comments / brackets / auto-closing / indentation), one editor theme per app
// theme (see MONACO_SPECS), and completion + hover providers.
//
// `registerBasis` is idempotent: it guards against the double-invocation that
// happens under React 18 StrictMode (mount → unmount → remount) and across
// multiple editor instances, so we never stack duplicate providers.

// We type against the live monaco namespace passed in, not a static import, so
// this module stays tree-shake-friendly and never pulls monaco at parse time.
import { THEMES } from "../theme";
import type { ThemeId } from "../theme";
import { KEYWORDS, TYPES, BUILTINS, LITERALS } from "./sealWords";

type Monaco = typeof import("monaco-editor");

export const BASIS_LANGUAGE_ID = "seal";

// One registration per monaco instance. WeakSet so a disposed monaco can GC.
const registered = new WeakSet<Monaco>();

/** One-line documentation surfaced by the hover provider. */
const DOCS: Record<string, string> = {
  // keywords
  contract: "Declares a contract, the top-level unit that compiles to a taproot address.",
  spend: "Declares a spend path. Each becomes a tapscript leaf; its parameters are the witness.",
  require: "States a spend obligation. A single predicate, or a `{ … }` block of conjoined predicates.",
  extern: "An externally-supplied constant, bound at instantiation time from the args file.",
  const: "A compile-time constant.",
  keypath: "Declares the taproot key-path spend (e.g. `PublicKey.MuSig2([…])` or `None` for a NUMS-disabled key path).",
  after: "Timelock obligation, the spend is only valid after the given absolute or relative lock time.",
  open: "Marks a parameter or path as open (unconstrained) in the certifier's model.",
  relaxed: "Relaxes the canonical-encoding requirement on a witness parameter.",
  in: "Binds the iteration variable in a comprehension, e.g. `k in keys`.",
  where: "Filters a comprehension, e.g. `count(v in votes where v => true)`.",
  // decorators
  depth: "`@depth(n)` pins this spend's leaf at tree depth n (0 to 128), bounding its control-block size.",
  weight: "`@weight(n)` sets this spend's relative usage weight for the tree planner; heavier paths sit shallower (cheaper to spend). Default 1.",
  // types
  Int: "A signed script integer (CScriptNum). Bounded where the certifier can infer a range.",
  Bool: "A boolean, script truthiness (empty / non-zero).",
  PublicKey: "A 32-byte x-only public key.",
  Signature: "A BIP-340 Schnorr signature.",
  Hash: "A hash digest value.",
  Bytes: "A byte string. `Bytes<N>` fixes the length to N bytes (e.g. `Bytes<32>`).",
  LockTime: "A lock time. `LockTime.Absolute` (height/MTP) or `LockTime.Relative` (blocks/seconds).",
  Sha256: "A SHA-256 digest type.",
  Hash160: "A HASH160 (SHA-256 then RIPEMD-160) digest type.",
  // builtins
  sha256: "sha256(x) → the SHA-256 digest of x.",
  hash160: "hash160(x) → RIPEMD-160(SHA-256(x)).",
  ripemd160: "ripemd160(x) → the RIPEMD-160 digest of x.",
  sum: "sum(x in xs => e) → the arithmetic sum of e over the collection.",
  count: "count(x in xs where p => e) → the number of elements for which the predicate holds.",
  fold: "fold(acc = init, x in xs => e) → a left fold accumulating e over the collection.",
  select: "select(cond, then: a, else: b) → a when cond holds, otherwise b. The labels are mandatory.",
  check: "key.check(sig) → true iff sig is a valid BIP-340 signature under key.",
  MuSig2: "MuSig2([keys]) → the aggregate of the given keys for the taproot key path.",
  None: "No key-path spend, the internal key is a NUMS point with no known discrete log.",
  // literals
  true: "Boolean true.",
  false: "Boolean false.",
};

// Inline signatures + snippet bodies for callable builtins and constructors, so
// completions read like a real IDE: a signature beside the name, and a call with
// parameter placeholders inserted on accept.
const SIGNATURES: Record<string, { sig: string; snippet: string }> = {
  sha256: { sig: "(bytes) → Sha256", snippet: "sha256(${1:bytes})" },
  hash160: { sig: "(bytes) → Hash160", snippet: "hash160(${1:bytes})" },
  ripemd160: { sig: "(bytes) → Bytes<20>", snippet: "ripemd160(${1:bytes})" },
  sum: { sig: "(x in xs => e) → Int", snippet: "sum(${1:x} in ${2:xs} => ${3:e})" },
  count: { sig: "(x in xs where p => e) → Int", snippet: "count(${1:x} in ${2:xs} where ${3:p} => ${4:e})" },
  fold: { sig: "(acc = init, x in xs => e) → Int", snippet: "fold(${1:acc} = ${2:init}, ${3:x} in ${4:xs} => ${5:e})" },
  select: { sig: "(cond, then: a, else: b) → T", snippet: "select(${1:cond}, then: ${2:a}, else: ${3:b})" },
  check: { sig: "(sig: Signature) → Bool", snippet: "check(${1:sig})" },
  MuSig2: { sig: "([PublicKey]) → PublicKey", snippet: "MuSig2([${1:keys}])" },
  Absolute: { sig: "(height | time) → LockTime", snippet: "Absolute(${1:height})" },
  Relative: { sig: "(blocks | seconds) → LockTime", snippet: "Relative(${1:blocks})" },
};

// Members offered after `receiver.` -- the "type a dot, get the methods"
// affordance. Keyed by the identifier before the dot, with a default for a value
// receiver (e.g. `owner.check`).
const MEMBERS: Record<string, string[]> = {
  PublicKey: ["MuSig2"],
  LockTime: ["Absolute", "Relative"],
};
const DEFAULT_MEMBERS = ["check"];

type MItem = import("monaco-editor").languages.CompletionItem;

/** DOCS as a markdown string so the `code` spans in them render in the panel. */
function docOf(name: string): import("monaco-editor").IMarkdownString | undefined {
  return DOCS[name] ? { value: DOCS[name] } : undefined;
}

/** The completion list. With a non-null `receiver` we are completing after a `.`
 *  and offer that receiver's members; otherwise the full vocabulary, with
 *  callables inserting their parameter placeholders. */
function completionItems(
  monaco: Monaco,
  range: import("monaco-editor").IRange,
  receiver: string | null,
): MItem[] {
  const k = monaco.languages.CompletionItemKind;
  const asSnippet = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;
  const items: MItem[] = [];

  // Member access (`obj.`): offer only that receiver's members, as calls.
  if (receiver !== null) {
    for (const name of MEMBERS[receiver] ?? DEFAULT_MEMBERS) {
      const s = SIGNATURES[name];
      const ctor = name === "Absolute" || name === "Relative";
      items.push({
        label: s ? { label: name, detail: ` ${s.sig}` } : name,
        kind: ctor ? k.Constructor : k.Method,
        insertText: s ? s.snippet : name,
        insertTextRules: s ? asSnippet : undefined,
        documentation: docOf(name),
        range,
      });
    }
    return items;
  }

  for (const kw of KEYWORDS) {
    items.push({ label: kw, kind: k.Keyword, insertText: kw, detail: "keyword", documentation: docOf(kw), range });
  }
  for (const ty of TYPES) {
    items.push({ label: ty, kind: k.Class, insertText: ty, detail: "type", documentation: docOf(ty), range });
  }
  for (const fn of BUILTINS) {
    const s = SIGNATURES[fn];
    items.push({
      label: s ? { label: fn, detail: ` ${s.sig}` } : fn,
      kind: k.Function,
      insertText: s ? s.snippet : fn,
      insertTextRules: s ? asSnippet : undefined,
      detail: s ? undefined : "builtin",
      documentation: docOf(fn),
      range,
    });
  }
  for (const lit of LITERALS) {
    items.push({ label: lit, kind: k.Constant, insertText: lit, detail: "literal", documentation: docOf(lit), range });
  }

  const snip = (
    label: string,
    insertText: string,
    detail: string,
    documentation: string,
    sortText: string,
  ): MItem => ({
    label,
    kind: k.Snippet,
    insertText,
    insertTextRules: asSnippet,
    detail,
    documentation: { value: documentation },
    sortText,
    range,
  });

  items.push(
    snip(
      "contract",
      ["contract ${1:Name} {", "\t$0", "", "\tkeypath None;", "}"].join("\n"),
      "scaffold",
      "Insert a contract scaffold with a key-path declaration.",
      "0contract",
    ),
    snip(
      "spend",
      ["spend ${1:name}(${2:signature: Signature}) {", "\trequire ${3:true};", "}"].join("\n"),
      "spend path",
      "Insert a spend path with a require obligation.",
      "0spend",
    ),
    snip("require", ["require {", "\t$0", "}"].join("\n"), "require block", "Insert a require block of conjoined predicates.", "0require"),
    snip("extern const", "extern const ${1:name}: ${2:PublicKey};", "extern constant", "Declare an externally-bound constant.", "0extern"),
    snip("keypath", "keypath ${1:None};", "key-path spend", "Declare the taproot key-path spend (or `None`).", "0keypath"),
    snip("after", "after ${1:LockTime.Absolute(${2:height})};", "timelock", "A timelock obligation on the spend path.", "0after"),
  );

  return items;
}

/** Editor themes, one per app theme id, matched by hex to each chrome palette.
 *  Token classes map to the Monarch tokens emitted above. Each uses its real
 *  reference theme's syntax palette (Tokyo Night, Catppuccin Mocha, Rosé Pine
 *  Moon, Night Owl, Dracula). */
const MONACO_SPECS: Record<ThemeId, import("monaco-editor").editor.IStandaloneThemeData> = {
  seal: {
    base: "vs",
    inherit: true,
    rules: [
      { token: "comment", foreground: "a8927b", fontStyle: "italic" },
      { token: "keyword", foreground: "c85f3c" },
      { token: "annotation", foreground: "bb8429" },
      { token: "type", foreground: "3f8d8f" },
      { token: "type.identifier", foreground: "3f8d8f" },
      { token: "support.function", foreground: "5a78bd" },
      { token: "constant.language", foreground: "a263b0" },
      { token: "string", foreground: "6a9c4a" },
      { token: "string.escape", foreground: "bb8429" },
      { token: "number", foreground: "a263b0" },
      { token: "number.hex", foreground: "a263b0" },
      { token: "operator", foreground: "c85f3c" },
      { token: "delimiter", foreground: "8a7460" },
      { token: "identifier", foreground: "574434" },
      { token: "identifier.member", foreground: "5a78bd" },
    ],
    colors: {
      "editor.background": "#fbeee2",
      "editor.foreground": "#574434",
      "editorLineNumber.foreground": "#cbb094",
      "editorLineNumber.activeForeground": "#8a7460",
      "editor.lineHighlightBackground": "#00000005",
      "editor.lineHighlightBorder": "#00000000",
      "editorCursor.foreground": "#ee9a76",
      "editor.selectionBackground": "#ee9a7636",
      "editor.inactiveSelectionBackground": "#ee9a7620",
      "editor.selectionHighlightBackground": "#ee9a7620",
      "editorIndentGuide.background1": "#efdcc7",
      "editorIndentGuide.activeBackground1": "#d9c2a4",
      "editorBracketMatch.background": "#ee9a7633",
      "editorBracketMatch.border": "#ee9a76",
      "editorWhitespace.foreground": "#ecd9c0",
      "editorGutter.background": "#fbeee2",
      "editorError.foreground": "#d66b54",
      "editorWarning.foreground": "#c6912f",
      "editorInfo.foreground": "#7f87c4",
      "editorWidget.background": "#f5e3d2",
      "editorWidget.border": "#ecd6bd",
      "editorSuggestWidget.background": "#f5e3d2",
      "editorSuggestWidget.border": "#ecd6bd",
      "editorSuggestWidget.foreground": "#574434",
      "editorSuggestWidget.selectedBackground": "#eed7c1",
      "editorSuggestWidget.selectedForeground": "#3f2f22",
      "editorSuggestWidget.selectedIconForeground": "#574434",
      "editorSuggestWidget.highlightForeground": "#df7d53",
      "editorSuggestWidget.focusHighlightForeground": "#c85f3c",
      "symbolIcon.keywordForeground": "#c85f3c",
      "symbolIcon.classForeground": "#3f8d8f",
      "symbolIcon.typeParameterForeground": "#3f8d8f",
      "symbolIcon.functionForeground": "#5a78bd",
      "symbolIcon.methodForeground": "#5a78bd",
      "symbolIcon.constructorForeground": "#3f8d8f",
      "symbolIcon.constantForeground": "#a263b0",
      "symbolIcon.snippetForeground": "#ee9a76",
      "editorHoverWidget.background": "#f5e3d2",
      "editorHoverWidget.border": "#ecd6bd",
      "scrollbarSlider.background": "#8a746026",
      "scrollbarSlider.hoverBackground": "#8a746044",
      "scrollbarSlider.activeBackground": "#8a746066",
    },
  },
  "tokyo-night": {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "565f89", fontStyle: "italic" },
      { token: "keyword", foreground: "bb9af7" },
      { token: "annotation", foreground: "e0af68" },
      { token: "type", foreground: "2ac3de" },
      { token: "type.identifier", foreground: "2ac3de" },
      { token: "support.function", foreground: "7aa2f7" },
      { token: "constant.language", foreground: "ff9e64" },
      { token: "string", foreground: "9ece6a" },
      { token: "string.escape", foreground: "89ddff" },
      { token: "number", foreground: "ff9e64" },
      { token: "number.hex", foreground: "ff9e64" },
      { token: "operator", foreground: "89ddff" },
      { token: "delimiter", foreground: "a9b1d6" },
      { token: "identifier", foreground: "c0caf5" },
      { token: "identifier.member", foreground: "7dcfff" },
    ],
    colors: {
      "editor.background": "#1a1b26",
      "editor.foreground": "#c0caf5",
      "editorLineNumber.foreground": "#3b4261",
      "editorLineNumber.activeForeground": "#737aa2",
      "editor.lineHighlightBackground": "#292e42",
      "editor.lineHighlightBorder": "#00000000",
      "editorCursor.foreground": "#c0caf5",
      "editor.selectionBackground": "#2e3c64",
      "editor.inactiveSelectionBackground": "#2e3c6480",
      "editor.selectionHighlightBackground": "#2e3c6480",
      "editorIndentGuide.background1": "#292e42",
      "editorIndentGuide.activeBackground1": "#3b4261",
      "editorBracketMatch.background": "#3b426166",
      "editorBracketMatch.border": "#7aa2f7",
      "editorWhitespace.foreground": "#3b4261",
      "editorGutter.background": "#1a1b26",
      "editorError.foreground": "#f7768e",
      "editorWarning.foreground": "#e0af68",
      "editorInfo.foreground": "#7aa2f7",
      "editorWidget.background": "#1f2335",
      "editorWidget.border": "#2a2e44",
      "editorSuggestWidget.background": "#1f2335",
      "editorSuggestWidget.border": "#2a2e44",
      "editorSuggestWidget.selectedBackground": "#2f334d",
      "editorHoverWidget.background": "#1f2335",
      "editorHoverWidget.border": "#2a2e44",
      "scrollbarSlider.background": "#9aa5ce33",
      "scrollbarSlider.hoverBackground": "#9aa5ce55",
      "scrollbarSlider.activeBackground": "#9aa5ce88",
    },
  },
  bitcoin: {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "6b6557", fontStyle: "italic" },
      { token: "keyword", foreground: "f7931a" },
      { token: "annotation", foreground: "e0a35c" },
      { token: "type", foreground: "f5c44d" },
      { token: "type.identifier", foreground: "f5c44d" },
      { token: "support.function", foreground: "5fb0d6" },
      { token: "constant.language", foreground: "cf9bdb" },
      { token: "string", foreground: "b0c94e" },
      { token: "string.escape", foreground: "e0a35c" },
      { token: "number", foreground: "cf9bdb" },
      { token: "number.hex", foreground: "cf9bdb" },
      { token: "operator", foreground: "e08f5c" },
      { token: "delimiter", foreground: "b8af9e" },
      { token: "identifier", foreground: "ece4d6" },
      { token: "identifier.member", foreground: "5fb0d6" },
    ],
    colors: {
      "editor.background": "#14130f",
      "editor.foreground": "#ece4d6",
      "editorLineNumber.foreground": "#40392a",
      "editorLineNumber.activeForeground": "#9c9486",
      "editor.lineHighlightBackground": "#ffffff06",
      "editor.lineHighlightBorder": "#00000000",
      "editorCursor.foreground": "#f7931a",
      "editor.selectionBackground": "#f7931a26",
      "editor.inactiveSelectionBackground": "#f7931a16",
      "editor.selectionHighlightBackground": "#f7931a16",
      "editorIndentGuide.background1": "#28241b",
      "editorIndentGuide.activeBackground1": "#40392a",
      "editorBracketMatch.background": "#f7931a1f",
      "editorBracketMatch.border": "#f7931a",
      "editorWhitespace.foreground": "#2c281f",
      "editorGutter.background": "#14130f",
      "editorError.foreground": "#f0584f",
      "editorWarning.foreground": "#f5c44d",
      "editorInfo.foreground": "#5fb0d6",
      "editorWidget.background": "#16140f",
      "editorWidget.border": "#2c281f",
      "editorSuggestWidget.background": "#16140f",
      "editorSuggestWidget.border": "#2c281f",
      "editorSuggestWidget.selectedBackground": "#28241b",
      "editorHoverWidget.background": "#16140f",
      "editorHoverWidget.border": "#2c281f",
      "scrollbarSlider.background": "#9c948633",
      "scrollbarSlider.hoverBackground": "#9c948655",
      "scrollbarSlider.activeBackground": "#9c948688",
    },
  },
  "catppuccin-mocha": {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "6c7086", fontStyle: "italic" },
      { token: "keyword", foreground: "cba6f7" },
      { token: "annotation", foreground: "fab387" },
      { token: "type", foreground: "f9e2af" },
      { token: "type.identifier", foreground: "f9e2af" },
      { token: "support.function", foreground: "89b4fa" },
      { token: "constant.language", foreground: "fab387" },
      { token: "string", foreground: "a6e3a1" },
      { token: "string.escape", foreground: "f5c2e7" },
      { token: "number", foreground: "fab387" },
      { token: "number.hex", foreground: "fab387" },
      { token: "operator", foreground: "94e2d5" },
      { token: "delimiter", foreground: "9399b2" },
      { token: "identifier", foreground: "cdd6f4" },
      { token: "identifier.member", foreground: "89b4fa" },
    ],
    colors: {
      "editor.background": "#1e1e2e",
      "editor.foreground": "#cdd6f4",
      "editorLineNumber.foreground": "#45475a",
      "editorLineNumber.activeForeground": "#cdd6f4",
      "editor.lineHighlightBackground": "#cdd6f40a",
      "editor.lineHighlightBorder": "#00000000",
      "editorCursor.foreground": "#f5e0dc",
      "editor.selectionBackground": "#585b7066",
      "editor.inactiveSelectionBackground": "#585b7040",
      "editor.selectionHighlightBackground": "#585b7040",
      "editorIndentGuide.background1": "#313244",
      "editorIndentGuide.activeBackground1": "#45475a",
      "editorBracketMatch.background": "#89b4fa1f",
      "editorBracketMatch.border": "#89b4fa",
      "editorWhitespace.foreground": "#45475a",
      "editorGutter.background": "#1e1e2e",
      "editorError.foreground": "#f38ba8",
      "editorWarning.foreground": "#f9e2af",
      "editorInfo.foreground": "#89b4fa",
      "editorWidget.background": "#1e1e2e",
      "editorWidget.border": "#313244",
      "editorSuggestWidget.background": "#1e1e2e",
      "editorSuggestWidget.border": "#313244",
      "editorSuggestWidget.selectedBackground": "#313244",
      "editorHoverWidget.background": "#1e1e2e",
      "editorHoverWidget.border": "#313244",
      "scrollbarSlider.background": "#6c708633",
      "scrollbarSlider.hoverBackground": "#6c708655",
      "scrollbarSlider.activeBackground": "#6c708688",
    },
  },
  "rose-pine-moon": {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "6e6a86", fontStyle: "italic" },
      { token: "keyword", foreground: "3e8fb0" },
      { token: "annotation", foreground: "ea9a97" },
      { token: "type", foreground: "c4a7e7" },
      { token: "type.identifier", foreground: "c4a7e7" },
      { token: "support.function", foreground: "9ccfd8" },
      { token: "constant.language", foreground: "ea9a97" },
      { token: "string", foreground: "f6c177" },
      { token: "string.escape", foreground: "ea9a97" },
      { token: "number", foreground: "ea9a97" },
      { token: "number.hex", foreground: "ea9a97" },
      { token: "operator", foreground: "908caa" },
      { token: "delimiter", foreground: "908caa" },
      { token: "identifier", foreground: "e0def4" },
      { token: "identifier.member", foreground: "9ccfd8" },
    ],
    colors: {
      "editor.background": "#232136",
      "editor.foreground": "#e0def4",
      "editorLineNumber.foreground": "#44415a",
      "editorLineNumber.activeForeground": "#908caa",
      "editor.lineHighlightBackground": "#2a283e",
      "editor.lineHighlightBorder": "#00000000",
      "editorCursor.foreground": "#e0def4",
      "editor.selectionBackground": "#44415a",
      "editor.inactiveSelectionBackground": "#44415a80",
      "editor.selectionHighlightBackground": "#44415a80",
      "editorIndentGuide.background1": "#393552",
      "editorIndentGuide.activeBackground1": "#56526e",
      "editorBracketMatch.background": "#c4a7e71f",
      "editorBracketMatch.border": "#c4a7e7",
      "editorWhitespace.foreground": "#44415a",
      "editorGutter.background": "#232136",
      "editorError.foreground": "#eb6f92",
      "editorWarning.foreground": "#f6c177",
      "editorInfo.foreground": "#9ccfd8",
      "editorWidget.background": "#2a283e",
      "editorWidget.border": "#393552",
      "editorSuggestWidget.background": "#2a283e",
      "editorSuggestWidget.border": "#393552",
      "editorSuggestWidget.selectedBackground": "#393552",
      "editorHoverWidget.background": "#2a283e",
      "editorHoverWidget.border": "#393552",
      "scrollbarSlider.background": "#6e6a8633",
      "scrollbarSlider.hoverBackground": "#6e6a8655",
      "scrollbarSlider.activeBackground": "#6e6a8688",
    },
  },
  "night-owl": {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "637777", fontStyle: "italic" },
      { token: "keyword", foreground: "c792ea" },
      { token: "annotation", foreground: "ecc48d" },
      { token: "type", foreground: "ffcb8b" },
      { token: "type.identifier", foreground: "ffcb8b" },
      { token: "support.function", foreground: "82aaff" },
      { token: "constant.language", foreground: "f78c6c" },
      { token: "string", foreground: "ecc48d" },
      { token: "string.escape", foreground: "f78c6c" },
      { token: "number", foreground: "f78c6c" },
      { token: "number.hex", foreground: "f78c6c" },
      { token: "operator", foreground: "7fdbca" },
      { token: "delimiter", foreground: "d6deeb" },
      { token: "identifier", foreground: "d6deeb" },
      { token: "identifier.member", foreground: "82aaff" },
    ],
    colors: {
      "editor.background": "#011627",
      "editor.foreground": "#d6deeb",
      "editorLineNumber.foreground": "#4b6479",
      "editorLineNumber.activeForeground": "#c5e7ff",
      "editor.lineHighlightBackground": "#0b2942",
      "editor.lineHighlightBorder": "#00000000",
      "editorCursor.foreground": "#80a4c2",
      "editor.selectionBackground": "#1d3b53",
      "editor.inactiveSelectionBackground": "#1d3b5380",
      "editor.selectionHighlightBackground": "#1d3b5380",
      "editorIndentGuide.background1": "#122d42",
      "editorIndentGuide.activeBackground1": "#1d3b53",
      "editorBracketMatch.background": "#82aaff1f",
      "editorBracketMatch.border": "#82aaff",
      "editorWhitespace.foreground": "#122d42",
      "editorGutter.background": "#011627",
      "editorError.foreground": "#ef5350",
      "editorWarning.foreground": "#ecc48d",
      "editorInfo.foreground": "#82aaff",
      "editorWidget.background": "#0b2942",
      "editorWidget.border": "#0e2a42",
      "editorSuggestWidget.background": "#0b2942",
      "editorSuggestWidget.border": "#0e2a42",
      "editorSuggestWidget.selectedBackground": "#102a44",
      "editorHoverWidget.background": "#0b2942",
      "editorHoverWidget.border": "#0e2a42",
      "scrollbarSlider.background": "#5f7e9744",
      "scrollbarSlider.hoverBackground": "#5f7e9766",
      "scrollbarSlider.activeBackground": "#5f7e9788",
    },
  },
  dracula: {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "6272a4", fontStyle: "italic" },
      { token: "keyword", foreground: "ff79c6" },
      { token: "annotation", foreground: "ffb86c" },
      { token: "type", foreground: "8be9fd" },
      { token: "type.identifier", foreground: "8be9fd" },
      { token: "support.function", foreground: "50fa7b" },
      { token: "constant.language", foreground: "bd93f9" },
      { token: "string", foreground: "f1fa8c" },
      { token: "string.escape", foreground: "bd93f9" },
      { token: "number", foreground: "bd93f9" },
      { token: "number.hex", foreground: "bd93f9" },
      { token: "operator", foreground: "ff79c6" },
      { token: "delimiter", foreground: "f8f8f2" },
      { token: "identifier", foreground: "f8f8f2" },
      { token: "identifier.member", foreground: "50fa7b" },
    ],
    colors: {
      "editor.background": "#282a36",
      "editor.foreground": "#f8f8f2",
      "editorLineNumber.foreground": "#6272a4",
      "editorLineNumber.activeForeground": "#f8f8f2",
      "editor.lineHighlightBackground": "#44475a40",
      "editor.lineHighlightBorder": "#00000000",
      "editorCursor.foreground": "#f8f8f2",
      "editor.selectionBackground": "#44475a",
      "editor.inactiveSelectionBackground": "#44475a80",
      "editor.selectionHighlightBackground": "#44475a80",
      "editorIndentGuide.background1": "#424450",
      "editorIndentGuide.activeBackground1": "#6272a4",
      "editorBracketMatch.background": "#bd93f91f",
      "editorBracketMatch.border": "#bd93f9",
      "editorWhitespace.foreground": "#424450",
      "editorGutter.background": "#282a36",
      "editorError.foreground": "#ff5555",
      "editorWarning.foreground": "#f1fa8c",
      "editorInfo.foreground": "#8be9fd",
      "editorWidget.background": "#21222c",
      "editorWidget.border": "#343746",
      "editorSuggestWidget.background": "#21222c",
      "editorSuggestWidget.border": "#343746",
      "editorSuggestWidget.selectedBackground": "#343746",
      "editorHoverWidget.background": "#21222c",
      "editorHoverWidget.border": "#343746",
      "scrollbarSlider.background": "#bd93f933",
      "scrollbarSlider.hoverBackground": "#bd93f955",
      "scrollbarSlider.activeBackground": "#bd93f988",
    },
  },
};

export function registerBasis(monaco: Monaco): void {
  if (registered.has(monaco)) return;
  registered.add(monaco);

  // Avoid re-registering the language id if it somehow already exists.
  if (!monaco.languages.getLanguages().some((l) => l.id === BASIS_LANGUAGE_ID)) {
    monaco.languages.register({
      id: BASIS_LANGUAGE_ID,
      extensions: [".sl"],
      aliases: ["Seal", "seal"],
    });
  }

  monaco.languages.setLanguageConfiguration(BASIS_LANGUAGE_ID, {
    comments: {
      lineComment: "//",
    },
    brackets: [
      ["{", "}"],
      ["[", "]"],
      ["(", ")"],
    ],
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"', notIn: ["string", "comment"] },
    ],
    surroundingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
    ],
    autoCloseBefore: ";:.,=}])> \n\t",
    indentationRules: {
      // Indent after an open brace/bracket/paren that isn't immediately closed.
      increaseIndentPattern: /^.*\{[^}"']*$|^.*\([^)"']*$|^.*\[[^\]"']*$/,
      decreaseIndentPattern: /^\s*[}\])].*$/,
    },
  });

  monaco.languages.setMonarchTokensProvider(BASIS_LANGUAGE_ID, {
    defaultToken: "",
    tokenPostfix: ".seal",

    keywords: KEYWORDS,
    typeKeywords: TYPES,
    builtins: BUILTINS,
    literals: LITERALS,

    // Namespaced members exposed as members (e.g. LockTime.Absolute) get
    // highlighted via the member rule below; the bare names are in `builtins`.
    operators: [
      "=",
      "==",
      "!=",
      "<",
      ">",
      "<=",
      ">=",
      "&&",
      "||",
      "!",
      "+",
      "-",
      "*",
      "/",
      "%",
      "=>",
      "..",
      ":",
    ],

    symbols: /[=><!~?:&|+\-*/^%.]+/,

    tokenizer: {
      root: [
        // Line comment (Seal has no block comments).
        [/\/\/.*$/, "comment"],

        // Decorators: @depth(n) / @weight(n). `[@]` is a literal @ (a bare
        // `@name` in a Monarch pattern would be read as a macro reference).
        [/[@][a-zA-Z_]\w*/, "annotation"],

        // 0x hex literals (with optional underscores).
        [/0[xX][0-9a-fA-F][0-9a-fA-F_]*/, "number.hex"],
        // Decimal fraction, e.g. @weight(0.4).
        [/\d[\d_]*\.\d[\d_]*/, "number"],
        // Decimal with underscore grouping, e.g. 1_000_000.
        [/\d[\d_]*/, "number"],

        // Strings.
        [/"/, "string", "@string"],

        // A `.member` access: highlight known constructors (Absolute, Relative,
        // MuSig2…) as builtins, otherwise as a plain member access.
        [
          /\.[A-Za-z_]\w*/,
          {
            cases: {
              "@default": "identifier.member",
            },
          },
        ],

        // Identifiers and keywords. Capital-initial unknown words read as types
        // (the Seal convention: types are PascalCase), so user-defined names
        // like `Multisig` after `contract` still read sensibly.
        [
          /[a-z_]\w*/,
          {
            cases: {
              "@keywords": "keyword",
              "@builtins": "support.function",
              "@literals": "constant.language",
              "@default": "identifier",
            },
          },
        ],
        [
          /[A-Z]\w*/,
          {
            cases: {
              "@typeKeywords": "type",
              "@builtins": "support.function",
              "@default": "type.identifier",
            },
          },
        ],

        // Brackets.
        [/[{}()[\]]/, "@brackets"],

        // Operators / punctuation.
        [
          /@symbols/,
          {
            cases: {
              "@operators": "operator",
              "@default": "",
            },
          },
        ],

        [/[;,]/, "delimiter"],

        // Whitespace.
        [/[ \t\r\n]+/, "white"],
      ],

      string: [
        [/[^\\"]+/, "string"],
        [/\\./, "string.escape"],
        [/"/, "string", "@pop"],
      ],
    },
  });

  for (const t of THEMES) {
    monaco.editor.defineTheme(t.monaco, MONACO_SPECS[t.id]);
  }

  monaco.languages.registerCompletionItemProvider(BASIS_LANGUAGE_ID, {
    triggerCharacters: ["."],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range: import("monaco-editor").IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };
      // Member access: if the char before the word is a `.`, complete the members
      // of the identifier before the dot rather than the global vocabulary.
      const line = model.getLineContent(position.lineNumber);
      let receiver: string | null = null;
      if (word.startColumn >= 2 && line[word.startColumn - 2] === ".") {
        const m = line.slice(0, word.startColumn - 2).match(/([A-Za-z_]\w*)\s*$/);
        receiver = m ? m[1] : "";
      }
      return { suggestions: completionItems(monaco, range, receiver) };
    },
  });

  monaco.languages.registerHoverProvider(BASIS_LANGUAGE_ID, {
    provideHover: (model, position) => {
      const word = model.getWordAtPosition(position);
      if (!word) return null;
      const doc = DOCS[word.word];
      if (!doc) return null;
      const range = new monaco.Range(
        position.lineNumber,
        word.startColumn,
        position.lineNumber,
        word.endColumn,
      );
      return {
        range,
        contents: [{ value: `**${word.word}**` }, { value: doc }],
      };
    },
  });
}
